import React, { useEffect, useState } from 'react';
import { X, CircleDot, ArrowRight, Calendar, User, Palette, Info, MapPin, AlertTriangle, Tag, Cake, Hash } from 'lucide-react';
import { GiHorseHead } from 'react-icons/gi';
import { FaHandHoldingMedical } from "react-icons/fa6";
import { getDocs, collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

// Fonction pour calculer l'âge
function getAge(dateNaissance) {
  if (!dateNaissance) return null;
  const naissance = new Date(dateNaissance);
  const now = new Date();
  let age = now.getFullYear() - naissance.getFullYear();
  const m = now.getMonth() - naissance.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < naissance.getDate())) {
    age--;
  }
  return age;
}

// Hook pour récupérer les couleurs de tags depuis Firebase
function useTagColors() {
  const [tagColors, setTagColors] = useState({});
  
  useEffect(() => {
    const loadTagColors = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'tagCouleursAnimaux'));
        const colors = {};
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.id && data.codeHex) {
            colors[data.id] = data.codeHex;
          }
        });
        setTagColors(colors);
      } catch (error) {
        console.error('Erreur chargement couleurs tags:', error);
        // Couleurs par défaut si erreur
        setTagColors({
          1: '#2563eb', // Bleu
          2: '#ef4444', // Rouge
          3: '#22c55e', // Vert
          4: '#eab308', // Jaune
          5: '#f97316', // Orange
          6: '#8b5cf6', // Violet
          7: '#ec4899', // Rose
          8: '#06b6d4', // Turquoise
          9: '#6b7280', // Gris
          10: '#111827', // Noir
          11: '#92400e', // Marron
          12: '#f3f4f6'  // Blanc
        });
      }
    };
    
    loadTagColors();
  }, []);
  
  return tagColors;
}

// Hook pour charger le nom du propriétaire
function useProprietaireName(proprietaireId) {
  const [proprietaireName, setProprietaireName] = useState(null);
  
  useEffect(() => {
    const loadProprietaire = async () => {
      if (!proprietaireId) {
        setProprietaireName(null);
        return;
      }
      
      try {
        const proprietaireDoc = await getDoc(doc(db, 'proprietaires', proprietaireId));
        if (proprietaireDoc.exists()) {
          const data = proprietaireDoc.data();
          setProprietaireName(`${data.nom} ${data.prenom}`);
        }
      } catch (error) {
        console.error('Erreur chargement propriétaire:', error);
        setProprietaireName(null);
      }
    };
    
    loadProprietaire();
  }, [proprietaireId]);
  
  return proprietaireName;
}

// Modal simple sans shadcn pour éviter les problèmes de z-index
const SimpleModal = ({ isOpen, onClose, children, title }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// Modal liste des chevaux
export const SimpleHorseListModal = ({ isOpen, onClose, zone, horses, onHorseSelect }) => {
  const tagColors = useTagColors();
  
  if (!zone) return null;

  // Fonction pour obtenir la couleur de surveillance la plus critique
  const getSurveillanceColor = (state) => {
    const colors = {
      rouge: "#ef4444",
      orange: "#fb923c",
      jaune: "#facc15",
      vert: "#22c55e"
    };
    return colors[state] || "#e5e7eb";
  };

  const getSurveillanceBackground = (state) => {
    const backgrounds = {
      rouge: "#fee2e2",
      orange: "#ffedd5",
      jaune: "#fef9c3",
      vert: "#dcfce7"
    };
    return backgrounds[state] || "#f3f4f6";
  };

  return (
    <SimpleModal 
      isOpen={isOpen} 
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <div 
            className="w-4 h-4 rounded-full" 
            style={{ backgroundColor: zone.color }}
          />
          Chevaux dans {zone.name}
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Type: {zone.type}</span>
          <span>Occupation: {horses.length}</span>
        </div>

        {horses.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <GiHorseHead className="h-12 w-12 text-gray-400 mb-2 mx-auto" />
            <p className="text-gray-500">Aucun cheval dans cette zone</p>
          </div>
        ) : (
          <div className="space-y-3">
            {horses.map((horse) => {
              const tagColorHex = tagColors[horse.tag] || '#e5e7eb';
              const age = getAge(horse.dateNaissance);
              const bgColor = getSurveillanceBackground(horse.SurveillanceEtat);
              
              return (
                <div
                  key={horse.id}
                  className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer flex items-center"
                  onClick={() => onHorseSelect(horse)}
                  style={{ backgroundColor: bgColor }}
                >
                  {/* Icône cheval avec couleur du tag */}
                  <div 
                    className="p-2 rounded-full mr-3"
                    style={{ 
                      backgroundColor: tagColorHex + '20' // Version transparente de la couleur
                    }}
                  >
                    <GiHorseHead 
                      className="h-6 w-6"
                      style={{ color: tagColorHex }}
                    />
                  </div>

                  {/* Centre : nom et infos */}
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold truncate">{horse.nom || 'Sans nom'}</div>
                    <div className="flex gap-3 mt-1 text-sm text-gray-600">
                      {age !== null && <span>{age} ans</span>}
                      {horse.sexe && <span>{horse.sexe}</span>}
                      {horse.couleur && <span>{horse.couleur}</span>}
                    </div>
                  </div>

                  {/* État de surveillance */}
                  {horse.SurveillanceEtat && (
                    <div 
                      className="w-3 h-3 rounded-full mr-3"
                      style={{ backgroundColor: getSurveillanceColor(horse.SurveillanceEtat) }}
                      title={`Surveillance: ${horse.SurveillanceEtat}`}
                    />
                  )}

                  {/* SIRE */}
                  <div className="text-xs text-gray-500">
                    {horse.sire ? `SIRE: ${horse.sire}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </SimpleModal>
  );
};

// Modal détails du cheval
export const SimpleHorseDetailsModal = ({
  isOpen,
  onClose,
  horse,
  zones,
  onMoveHorse,
  onStartMoveFromMap
}) => {
  const [selectedTargetZone, setSelectedTargetZone] = React.useState('');
  const tagColors = useTagColors();
  const navigate = useNavigate();
  const proprietaireName = useProprietaireName(horse?.proprietaire);

  if (!horse) return null;

  const handleMove = () => {
    if (selectedTargetZone) {
      onMoveHorse(selectedTargetZone);
      setSelectedTargetZone('');
    }
  };

  const handleViewDetails = () => {
    navigate(`/animaux/${horse.id}`);
  };

  const handleReportProblem = () => {
    // Fermer ce modal et ouvrir le modal de signalement
    onClose();
    // Ouvrir le modal de signalement (géré dans le parent)
    if (window.onOpenReportProblem) {
      window.onOpenReportProblem(horse);
    }
  };

  const handleMedicalTicket = () => {
    // Fermer ce modal et ouvrir le modal de ticket médical
    onClose();
    // Ouvrir le modal de ticket médical (géré dans le parent)
    if (window.onOpenMedicalTicket) {
      window.onOpenMedicalTicket(horse);
    }
  };

  const tagColorHex = tagColors[horse.tag] || '#e5e7eb';
  const age = getAge(horse.dateNaissance);

  return (
    <SimpleModal 
      isOpen={isOpen} 
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <GiHorseHead className="h-5 w-5 text-blue-600" />
          Détails du cheval
        </div>
      }
    >
      <div className="space-y-6">
        {/* En-tête avec nom et icône */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold">{horse.nom || 'Sans nom'}</h2>
            <p className="text-gray-600 mt-1">
              {horse.race && <span>{horse.race} • </span>}
              {age !== null && <span>{age} ans • </span>}
              {horse.sexe && <span>{horse.sexe}</span>}
            </p>
          </div>
          <div 
            className="p-4 rounded-full"
            style={{ 
              backgroundColor: tagColorHex + '20' // Version transparente
            }}
          >
            <GiHorseHead 
              className="h-12 w-12"
              style={{ color: tagColorHex }}
            />
          </div>
        </div>

        {/* Grille d'informations */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="text-sm text-gray-500">N° SIRE</label>
            <p className="font-medium">{horse.sire || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Transpondeur</label>
            <p className="font-medium">{horse.transpondeur || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Couleur</label>
            <p className="font-medium">{horse.couleur || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Catégorie</label>
            <p className="font-medium">{horse.categorie || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Propriétaire</label>
            <p className="font-medium">{proprietaireName || '-'}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Date de naissance</label>
            <p className="font-medium">
              {horse.dateNaissance 
                ? new Date(horse.dateNaissance).toLocaleDateString('fr-FR')
                : '-'}
            </p>
          </div>
        </div>

        {/* NOUVEAU : Boutons Consulter, Signaler et Ticket Médical */}
        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={handleViewDetails}
            className="p-4 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition flex flex-col items-center gap-2"
          >
            <Info className="h-6 w-6" />
            <span className="font-medium text-sm">Consulter fiche</span>
          </button>
          
          <button
            onClick={handleReportProblem}
            className="p-4 border-2 border-orange-500 text-orange-600 rounded-lg hover:bg-orange-50 transition flex flex-col items-center gap-2"
          >
            <AlertTriangle className="h-6 w-6" />
            <span className="font-medium text-sm">Signaler problème</span>
          </button>

          <button
            onClick={handleMedicalTicket}
            className="p-4 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition flex flex-col items-center gap-2"
          >
            <FaHandHoldingMedical className="h-6 w-6" />
            <span className="font-medium text-sm">Ticket médical</span>
          </button>
        </div>

        {/* Section déplacement */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium mb-3">Déplacer le cheval</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Choisir la zone de destination
              </label>
              <select 
                value={selectedTargetZone} 
                onChange={(e) => setSelectedTargetZone(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sélectionner une zone</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name} ({zone.type})
                  </option>
                ))}
              </select>
            </div>

            {selectedTargetZone && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Le cheval sera déplacé vers la zone sélectionnée après confirmation.
                </p>
              </div>
            )}

            {onStartMoveFromMap && (
              <button
                className="w-full mt-2 px-4 py-2 border border-blue-600 text-blue-700 rounded-md hover:bg-blue-50 transition"
                onClick={() => onStartMoveFromMap(horse)}
              >
                Déplacer depuis la carte
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Fermer
          </button>
          <button 
            onClick={handleMove}
            disabled={!selectedTargetZone}
            className={`px-4 py-2 rounded-md flex items-center gap-2 ${
              selectedTargetZone 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <ArrowRight className="h-4 w-4" />
            Déplacer le cheval
          </button>
        </div>
      </div>
    </SimpleModal>
  );
};